import { useState, useEffect } from "react";
import DashboardLayout from "../../Layout/DashboardLayout";
import PageHeader from "../../Common/PageHeader";
import KPICards from "./ProjectManagerDashboard/KPICards";
import LabKPICards from "./LabManagerDashboard/KPICards";
import TestEngineerKPICards from "./TestEngineerDashboard/KPICards";
import ProjectWiseHours from "./ProjectManagerDashboard/ProjectWiseHours";
import TestTypeAnalysis from "./ProjectManagerDashboard/TestTypeAnalysis";
import LabWiseProject from "./ProjectManagerDashboard/LabWiseProject";
import LabWiseTestTypeAnalysis from "./ProjectManagerDashboard/LabWiseTestTypeAnalysis";
import HappinessIndexDashboard from "./HappinessIndex/HappinessIndexDashboard";
import LabOccupancyHeatmap from "./LabManagerDashboard/LabOccupancyHeatmap";
import LabUtilizationRanking from "./LabManagerDashboard/LabUtilizationRanking";
import BookingPipelineChart from "./LabManagerDashboard/BookingPipeline";
import { companyID } from "../../Common/UIComponents";
import ResourceUtilization from "./TestEngineerDashboard/ProjectResourseUtilization";
import MPDashboardIndex from "./MPDashboard/MPDashboardIndex";

const DashboardMain = () => {
  // Get role code from localStorage
  const roleCode = localStorage.getItem("roleCode") || "";
  const [selectedProjectID, setSelectedProjectID] = useState(0);
  const [selectedProjectName, setSelectedProjectName] = useState("");
  const [projectWiseChartHeight, setProjectWiseChartHeight] = useState(400);
  const [selectedFacilityID, setSelectedFacilityID] = useState(0);
  const [facilityList, setFacilityList] = useState([]);
  const [projectList, setProjectList] = useState([]);

  // Check if user is Project Manager
  const isProjectManager = roleCode === "PM";
  const isLabManager = roleCode === "LM";
  const isTestEngineer = roleCode === "TO";
  const isAdmin = roleCode === "AD";
  const isProjectResponsible = roleCode === "PR";

  // Show access denied if no valid role
  if (!isProjectManager && !isLabManager && !isTestEngineer && !isAdmin) {
    return (
      <DashboardLayout>
        <div className="p-6 bg-slate-50 min-h-screen flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 text-center shadow-sm border border-gray-200">
            <span className="material-symbols-outlined text-5xl text-red-500 mb-4 block">
              lock
            </span>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Access Denied
            </h2>
            <p className="text-gray-600">
              You don't have permission to view the dashboard.
            </p>
            <p className="text-sm text-gray-500 mt-4">
              Current Role: {roleCode || "Unknown"}
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 bg-slate-50 min-h-screen">
        {/* <PageHeader
          title={
            isAdmin
              ? "Admin Dashboard"
              : isProjectManager
                ? "Project Manager Dashboard"
                : isLabManager
                  ? "Lab Manager Dashboard"
                  : "Test Engineer Dashboard"
          }
        /> */}

        <div className="space-y-6">
          {/* ══════════════════════════════════════════════════════════════ */}
          {/* Happiness Index */}
          {/* ══════════════════════════════════════════════════════════════ */}
          <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow duration-300">
            <div className="border-l-4 border-l-[#08757C] flex items-center gap-3 px-6 pt-6 pb-4 border-b border-slate-100">
              <div className="h-11 w-11 rounded-lg bg-[#08757C]/15 flex items-center justify-center flex-shrink-0">
                <span className="material-symbols-outlined text-[#08757C] text-2xl font-bold">
                  sentiment_satisfied
                </span>
              </div>
              <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                Happiness Index
              </h2>
            </div>
            <div className="p-6">
              <HappinessIndexDashboard />
            </div>
          </div>
          {/* ════════════════════════════════════════════════════════════════ */}
          {/* PROJECT MANAGER DASHBOARD */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {(isProjectManager || isAdmin) && (
            <>
              {/* Section 1: KPI Cards */}
              <div className="bg-white rounded-xl p-6 shadow-md border border-slate-200 hover:shadow-lg transition-shadow duration-300">
                <div className="border-l-4 border-l-[#08757C] pl-4 flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                  <div className="h-11 w-11 rounded-lg bg-[#08757C]/15 flex items-center justify-center flex-shrink-0">
                    <span className="material-symbols-outlined text-[#08757C] text-2xl font-bold">
                      speed
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                    Project Details
                  </h2>
                </div>
                <KPICards />
              </div>

              {/* Section 2 & 3: Project Analytics (2 Charts Side by Side) */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Chart 1: Project-wise Hours Consumption */}
                <div>
                  <ProjectWiseHours
                    onProjectSelected={(projectID, projectName) => {
                      console.log(
                        "🔥 DashboardMain received - ProjectID:",
                        projectID,
                        "ProjectName:",
                        projectName,
                      );
                      setSelectedProjectID(projectID);
                      setSelectedProjectName(projectName || "");
                      console.log(
                        "✅ State set - ProjectID:",
                        projectID,
                        "Name:",
                        projectName,
                      );
                    }}
                    onProjectListLoaded={setProjectList}
                    selectedProjectID={selectedProjectID}
                    onChartHeightChange={setProjectWiseChartHeight}
                  />
                </div>

                {/* Chart 2: Test Type Analysis */}
                <div>
                  <TestTypeAnalysis
                    selectedProjectID={selectedProjectID}
                    selectedProjectName={selectedProjectName}
                    minHeight={projectWiseChartHeight}
                    projectListProp={projectList}
                    onClearFilter={() => {
                      setSelectedProjectID(0);
                      setSelectedProjectName("");
                    }}
                    onProjectSelected={(projectID, projectName) => {
                      setSelectedProjectID(projectID);
                      setSelectedProjectName(projectName || "");
                    }}
                  />
                </div>
              </div>

              {/* Section 4 & 5: Lab-wise Utilization (Full Width Initially, Half Width When Lab Selected) */}
              <div
                className={`transition-all duration-700 ease-out ${selectedFacilityID > 0 ? "grid grid-cols-1 lg:grid-cols-2 gap-6" : ""}`}
              >
                {/* Chart 1: Lab-wise Project Utilization (Width animates when lab selected) */}
                <div className="transition-all duration-700 ease-out overflow-hidden">
                  <LabWiseProject
                    selectedFacilityID={selectedFacilityID}
                    onFacilitySelected={setSelectedFacilityID}
                    onFacilityListLoaded={setFacilityList}
                    onClearSelection={() => setSelectedFacilityID(0)}
                  />
                </div>

                {/* Chart 2: Lab-wise Test Type Analysis (Fades and appears) */}
                {selectedFacilityID > 0 && (
                  <div
                    key={`test-type-${selectedFacilityID}`}
                    className="transition-opacity duration-700 ease-out opacity-0 lg:opacity-100"
                  >
                    <LabWiseTestTypeAnalysis
                      selectedFacilityID={selectedFacilityID}
                      facilityList={facilityList}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* LAB MANAGER DASHBOARD */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {(isLabManager || isAdmin) && (
            <>
              {/* Section 1: KPI Cards */}
              <div className="bg-white rounded-xl p-6 shadow-md border border-slate-200 hover:shadow-lg transition-shadow duration-300">
                <div className="border-l-4 border-l-[#08757C] pl-4 flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                  <div className="h-11 w-11 rounded-lg bg-[#08757C]/15 flex items-center justify-center flex-shrink-0">
                    <span className="material-symbols-outlined text-[#08757C] text-2xl font-bold">
                      speed
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                    Lab Details
                  </h2>
                </div>
                <LabKPICards />
              </div>

              {/* Section 2: Lab Occupancy Heatmap */}
              {/* <div className="bg-white rounded-xl p-6 shadow-md border border-slate-200 hover:shadow-lg transition-shadow duration-300">
                <div className="border-l-4 border-l-[#08757C] pl-4 flex items-center gap-3 mb-4 pb-4 border-b border-slate-100">
                  <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                    Lab Occupancy Heatmap
                  </h2>
                </div>
                <LabOccupancyHeatmap />
              </div> */}
              <div>
                <LabUtilizationRanking />
              </div>
              <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <div className="border-l-4 border-l-[#08757C] flex items-center gap-3 px-6 pt-6 pb-4 border-b border-slate-100">
                  <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                    Booking Pipeline
                  </h2>
                </div>
                <div className="p-6">
                  <BookingPipelineChart companyId={companyID} />
                </div>
              </div>
            </>
          )}

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* TEST ENGINEER DASHBOARD */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {isTestEngineer && (
            <>
              {/* Section 1: KPI Cards */}
              <div className="bg-white rounded-xl p-6 shadow-md border border-slate-200 hover:shadow-lg transition-shadow duration-300">
                <div className="border-l-4 border-l-[#08757C] pl-4 flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                  <div className="h-11 w-11 rounded-lg bg-[#08757C]/15 flex items-center justify-center flex-shrink-0">
                    <span className="material-symbols-outlined text-[#08757C] text-2xl font-bold">
                      speed
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                    Key Performance Indicators
                  </h2>
                </div>
                <TestEngineerKPICards />
              </div>

              <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <div className="border-l-4 border-l-[#08757C] flex items-center gap-3 px-6 pt-6 pb-4 border-b border-slate-100">
                  <span className="material-symbols-outlined text-[#08757C] text-2xl font-bold">
                    engineering
                  </span>
                  <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                    Resource Utilization
                  </h2>
                </div>
                <div className="p-6">
                  <ResourceUtilization />
                </div>
              </div>
            </>
          )}
          {(isProjectResponsible || isAdmin) && (
            <div className="bg-white rounded-xl p-6 shadow-md border border-slate-200 hover:shadow-lg transition-shadow duration-300">
              <div className="border-l-4 border-l-[#08757C] pl-4 flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                  Measurement Protocol Approval Request
                </h2>
              </div>
              <MPDashboardIndex />
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardMain;
